import { useCallback, useEffect, useState } from 'react'
import { doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore'

import {
  releaseFeedSchema,
  releaseReadStateSchema,
  type ReleaseFeed,
  type ReleaseFeedEntry,
  type ReleaseReadState,
} from '@hapana/shared/schemas/release-hub'
import { readKeyOf, unreadEntries } from '@hapana/shared/utils/release-hub.utils'
import useAuthStore from '_stores/auth.store'

import { db } from '../config/firebase.config'

const FEED_COLLECTION = 'releaseFeed'
const FEED_DOC = 'current'
const READS_COLLECTION = 'releaseReads'
const READS_SUBCOLLECTION = 'users'
const MAX_READ_KEYS = 400

type FirebaseUser = { uid: string; tenantId: string | null }

export type ReleaseReadStateApi = {
  unreadCount: number
  isUnread: (entry: Pick<ReleaseFeedEntry, 'id' | 'stage'>) => boolean
  markRead: (entry: Pick<ReleaseFeedEntry, 'id' | 'stage'>) => void
  markAllRead: () => void
}

function reportListenerError(path: string) {
  return (error: { code?: string; message?: string }) => {
    // eslint-disable-next-line no-console -- realtime is best-effort; a silent permission-denied hides for hours (see the check-in widget)
    console.error('[release hub] listener error on %s (%s): %s', path, error?.code, error?.message)
  }
}

export function useReleaseReadState(enabled: boolean): ReleaseReadStateApi {
  const user = useAuthStore((state: { user: FirebaseUser | null }) => state.user)
  const [feed, setFeed] = useState<ReleaseFeed | null>(null)
  const [readState, setReadState] = useState<ReleaseReadState | null>(null)

  const tenantId = user?.tenantId ?? null
  const uid = user?.uid ?? null

  useEffect(() => {
    if (!enabled || !uid) {
      return undefined
    }
    return onSnapshot(
      doc(db, FEED_COLLECTION, FEED_DOC),
      (snapshot) => {
        const parsed = releaseFeedSchema.safeParse(snapshot.data())
        setFeed(parsed.success ? parsed.data : null)
      },
      reportListenerError(`${FEED_COLLECTION}/${FEED_DOC}`)
    )
  }, [enabled, uid])

  useEffect(() => {
    if (!enabled || !uid || !tenantId) {
      return undefined
    }
    return onSnapshot(
      doc(db, READS_COLLECTION, tenantId, READS_SUBCOLLECTION, uid),
      (snapshot) => {
        const parsed = releaseReadStateSchema.safeParse({
          readKeys: snapshot.get('readKeys') ?? [],
          readAllBefore: snapshot.get('readAllBefore') ?? null,
        })
        setReadState(parsed.success ? parsed.data : null)
      },
      reportListenerError(`${READS_COLLECTION}/${tenantId}/${READS_SUBCOLLECTION}`)
    )
  }, [enabled, uid, tenantId])

  const unread = unreadEntries(feed, readState)
  const unreadKeys = new Set(unread.map(readKeyOf))

  const writeReadState = useCallback(
    (next: ReleaseReadState) => {
      if (!uid || !tenantId) {
        return
      }
      setReadState(next)
      void setDoc(doc(db, READS_COLLECTION, tenantId, READS_SUBCOLLECTION, uid), {
        ...next,
        updatedAt: serverTimestamp(),
      }).catch(reportListenerError(`${READS_COLLECTION}/${tenantId}/${READS_SUBCOLLECTION}`))
    },
    [uid, tenantId]
  )

  const markRead = useCallback(
    (entry: Pick<ReleaseFeedEntry, 'id' | 'stage'>) => {
      if (!readState) {
        return
      }
      const key = readKeyOf(entry)
      const current = readState.readKeys
      if (current.includes(key)) {
        return
      }
      const liveKeys = new Set((feed?.entries ?? []).map(readKeyOf))
      const kept =
        current.length >= MAX_READ_KEYS ? current.filter((k) => liveKeys.has(k)) : current
      writeReadState({ readKeys: [...kept, key], readAllBefore: readState.readAllBefore })
    },
    [feed, readState, writeReadState]
  )

  const markAllRead = useCallback(() => {
    writeReadState({ readKeys: [], readAllBefore: new Date().toISOString() })
  }, [writeReadState])

  return {
    unreadCount: unread.length,
    isUnread: (entry) => unreadKeys.has(readKeyOf(entry)),
    markRead,
    markAllRead,
  }
}
